import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiX, FiStar, FiTrendingUp, FiZap } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const EMPTY = {
  name: '', brand: '', price: '', originalPrice: '', category: 'women', subcategory: '',
  sizes: '', colors: '', stock: '', description: '', imageEmoji: '👗', imageUrl: '',
  rating: '', material: '', fit: '', washCare: '',
  isNew: false, isFeatured: false, isTrending: false,
};
const CATEGORIES = ['men', 'women', 'kids', 'home', 'beauty', 'studio', 'accessories', 'footwear', 'ethnic'];
const PER_PAGE = 15;

function FlagChip({ active, color, label, icon: Icon, onClick }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(); }}
      title={`Toggle ${label}`}
      style={{
        padding: '3px 8px', borderRadius: 20, border: `1px solid ${active ? color : '#E5E7EB'}`,
        background: active ? `${color}18` : 'transparent',
        color: active ? color : '#9CA3AF', fontSize: 11, fontWeight: 700, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 3, transition: 'all 0.15s',
      }}
    >
      <Icon size={10} /> {label}
    </button>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, size: PER_PAGE });
      if (search) params.append('search', search);
      if (categoryFilter !== 'ALL') params.append('category', categoryFilter);
      const res = await api.get(`/api/admin/products?${params}`);
      const data = res.data;
      if (data.content) {
        setProducts(data.content);
        setTotal(data.totalElements);
        setTotalPages(data.totalPages);
      } else {
        setProducts(Array.isArray(data) ? data : []);
        setTotal(Array.isArray(data) ? data.length : 0);
        setTotalPages(1);
      }
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [page, search, categoryFilter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openAdd = () => { setEditProduct(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (p) => {
    setEditProduct(p);
    setForm({
      ...EMPTY, ...p,
      sizes: Array.isArray(p.sizes) ? p.sizes.join(', ') : p.sizes || '',
      colors: Array.isArray(p.colors) ? p.colors.join(', ') : p.colors || '',
      highlights: Array.isArray(p.highlights) ? p.highlights.join(', ') : p.highlights || '',
      isNew: p.isNew || false,
      isFeatured: p.isFeatured || false,
      isTrending: p.isTrending || false,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.brand || !form.price) { toast.error('Name, brand and price are required'); return; }
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      originalPrice: parseFloat(form.originalPrice) || null,
      stock: parseInt(form.stock) || 0,
      rating: parseFloat(form.rating) || null,
      sizes: form.sizes ? form.sizes.split(',').map(s => s.trim()).filter(Boolean) : [],
      colors: form.colors ? form.colors.split(',').map(c => c.trim()).filter(Boolean) : [],
      highlights: form.highlights ? form.highlights.split(',').map(h => h.trim()).filter(Boolean) : [],
    };
    try {
      if (editProduct) {
        const res = await api.put(`/api/admin/products/${editProduct.id}`, payload);
        setProducts(prev => prev.map(p => p.id === editProduct.id ? res.data : p));
        toast.success('Product updated!');
      } else {
        await api.post('/api/admin/products', payload);
        toast.success('Product added!');
        fetchProducts();
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await api.delete(`/api/admin/products/${id}`);
      setProducts(prev => prev.filter(p => p.id !== id));
      setTotal(t => t - 1);
      toast.success('Product deleted!');
    } catch { toast.error('Delete failed'); }
  };

  const toggleFlag = async (product, flag) => {
    const newVal = !product[flag];
    try {
      await api.put(`/api/admin/products/${product.id}/flags`, { [flag]: newVal });
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, [flag]: newVal } : p));
      const labels = { isNew: 'New', isFeatured: 'Featured', isTrending: 'Trending' };
      toast.success(`${labels[flag]} ${newVal ? 'enabled' : 'disabled'} for ${product.name}`);
    } catch { toast.error('Failed to update flag'); }
  };

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <h3>Products <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>({total})</span></h3>
          <div className="admin-card-actions">
            <select className="admin-select" value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(0); }}>
              <option value="ALL">All Categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
            <div className="admin-search">
              <FiSearch />
              <input placeholder="Search products..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={openAdd}><FiPlus /> Add Product</button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #E5E7EB', borderTopColor: '#7C3AED', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
            Loading products...
          </div>
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Product</th><th>Category</th><th>Price</th><th>Stock</th>
                    <th>Homepage Flags</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No products found</td></tr>
                  ) : products.map(p => {
                    const disc = p.originalPrice && p.price ? Math.round((1 - p.price / p.originalPrice) * 100) : 0;
                    return (
                      <tr key={p.id}>
                        <td>
                          <div className="product-table-info">
                            <div className="product-table-image">
                              {p.imageUrl ? <img src={p.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (p.imageEmoji || '👗')}
                            </div>
                            <div>
                              <span className="product-table-name">{p.name}</span>
                              <span className="product-table-brand">{p.brand}</span>
                            </div>
                          </div>
                        </td>
                        <td><span className="table-badge badge-blue">{p.category}</span></td>
                        <td>
                          <div>
                            <span style={{ fontWeight: 700 }}>₹{Number(p.price).toLocaleString()}</span>
                            {p.originalPrice && <span style={{ fontSize: 12, color: 'var(--text-muted)', textDecoration: 'line-through', marginLeft: 6 }}>₹{Number(p.originalPrice).toLocaleString()}</span>}
                            {disc > 0 && <span style={{ fontSize: 11, color: '#10B981', marginLeft: 4 }}>{disc}% off</span>}
                          </div>
                        </td>
                        <td style={{ color: p.stock === 0 ? '#EF4444' : 'var(--text-primary)', fontWeight: 600 }}>
                          {p.stock === 0 ? 'Out of stock' : p.stock}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <FlagChip active={p.isFeatured} color="#7C3AED" label="Featured" icon={FiStar} onClick={() => toggleFlag(p, 'isFeatured')} />
                            <FlagChip active={p.isTrending} color="#F59E0B" label="Trending" icon={FiTrendingUp} onClick={() => toggleFlag(p, 'isTrending')} />
                            <FlagChip active={p.isNew} color="#10B981" label="New" icon={FiZap} onClick={() => toggleFlag(p, 'isNew')} />
                          </div>
                        </td>
                        <td>
                          <span className={`table-badge ${p.status === 'ACTIVE' ? 'badge-green' : p.status === 'OUT_OF_STOCK' ? 'badge-yellow' : 'badge-pink'}`}>
                            {p.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button className="table-action-btn edit" onClick={() => openEdit(p)}><FiEdit2 /></button>
                            <button className="table-action-btn delete" onClick={() => handleDelete(p.id)}><FiTrash2 /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="admin-pagination">
                <p>Showing {page * PER_PAGE + 1}–{Math.min((page + 1) * PER_PAGE, total)} of {total}</p>
                <div className="admin-pagination-btns">
                  <button className="page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹</button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    const p = totalPages <= 7 ? i : page < 4 ? i : page > totalPages - 4 ? totalPages - 7 + i : page - 3 + i;
                    return (
                      <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>{p + 1}</button>
                    );
                  })}
                  <button className="page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>›</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && setModalOpen(false)}>
          <div className="admin-modal" style={{ maxWidth: 720 }}>
            <div className="admin-modal-header">
              <h3>{editProduct ? 'Edit Product' : 'Add New Product'}</h3>
              <button className="modal-close-btn" onClick={() => setModalOpen(false)}><FiX /></button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-grid-2">
                <div className="admin-input-group"><label>Product Name *</label><input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Floral Print Maxi Dress" /></div>
                <div className="admin-input-group"><label>Brand *</label><input value={form.brand} onChange={e => setForm(p => ({ ...p, brand: e.target.value }))} placeholder="e.g. Zara" /></div>
                <div className="admin-input-group"><label>Selling Price (₹) *</label><input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} placeholder="2499" /></div>
                <div className="admin-input-group"><label>MRP / Original Price (₹)</label><input type="number" value={form.originalPrice} onChange={e => setForm(p => ({ ...p, originalPrice: e.target.value }))} placeholder="4999" /></div>
                <div className="admin-input-group">
                  <label>Category *</label>
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div className="admin-input-group"><label>Sub-category</label><input value={form.subcategory} onChange={e => setForm(p => ({ ...p, subcategory: e.target.value }))} placeholder="e.g. dresses, shirts" /></div>
                <div className="admin-input-group"><label>Stock Quantity</label><input type="number" value={form.stock} onChange={e => setForm(p => ({ ...p, stock: e.target.value }))} placeholder="100" /></div>
                <div className="admin-input-group"><label>Rating (0-5)</label><input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={e => setForm(p => ({ ...p, rating: e.target.value }))} placeholder="4.5" /></div>
                <div className="admin-input-group"><label>Sizes (comma separated)</label><input value={form.sizes} onChange={e => setForm(p => ({ ...p, sizes: e.target.value }))} placeholder="XS, S, M, L, XL" /></div>
                <div className="admin-input-group"><label>Colors (comma separated)</label><input value={form.colors} onChange={e => setForm(p => ({ ...p, colors: e.target.value }))} placeholder="Red, Blue, Black" /></div>
                <div className="admin-input-group"><label>Image Emoji</label><input value={form.imageEmoji} onChange={e => setForm(p => ({ ...p, imageEmoji: e.target.value }))} placeholder="👗" /></div>
                <div className="admin-input-group"><label>Material</label><input value={form.material} onChange={e => setForm(p => ({ ...p, material: e.target.value }))} placeholder="100% Cotton" /></div>
              </div>

              <div className="admin-input-group">
                <label>Product Image URL</label>
                <input value={form.imageUrl} onChange={e => setForm(p => ({ ...p, imageUrl: e.target.value }))} placeholder="https://images.unsplash.com/... or any direct image URL" />
                {form.imageUrl && <img src={form.imageUrl} alt="Preview" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, marginTop: 8, border: '1px solid var(--border)' }} onError={e => e.target.style.display='none'} />}
              </div>

              <div className="admin-input-group"><label>Description</label><textarea rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Product description..." /></div>
              <div className="admin-input-group"><label>Highlights (comma separated)</label><input value={form.highlights} onChange={e => setForm(p => ({ ...p, highlights: e.target.value }))} placeholder="Slim Fit, 100% Cotton, Machine Wash" /></div>

              <div style={{ marginTop: 16 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Homepage Visibility</p>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {[
                    { key: 'isFeatured', label: 'Featured Picks', color: '#7C3AED', icon: '⭐' },
                    { key: 'isTrending', label: 'Trending Now', color: '#F59E0B', icon: '🔥' },
                    { key: 'isNew', label: 'New Arrivals', color: '#10B981', icon: '✨' },
                  ].map(flag => (
                    <label key={flag.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: form[flag.key] ? flag.color : 'var(--text-secondary)' }}>
                      <input type="checkbox" checked={form[flag.key] || false} onChange={e => setForm(p => ({ ...p, [flag.key]: e.target.checked }))} style={{ width: 16, height: 16, accentColor: flag.color }} />
                      {flag.icon} {flag.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editProduct ? 'Update Product' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
